import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Database, Eye, Loader2, MessageSquare, ShieldCheck, Sparkles } from "lucide-react";
import * as React from "react";

import { Badge, Button, cn } from "@ando/ui";

import type { AdminOrgCostDimensionRow, AdminOrgCostUserRow } from "@ando/core";

import { AdminKpiGrid } from "@/features/admin/components/AdminKpiCard";
import { useActivateOrgCostInspect, useAdminOrgCost } from "@/features/admin/hooks/useAdminOrg";
import { formatInt, formatUsd } from "@/features/admin/lib/adminFormat";

/**
 * Per-org cost drill-down tab (Jun 5, 2026).
 *
 * BE: ``GET /admin/v2/orgs/{org_id}/cost`` (``admin_v2.py``). Single source of
 * truth = ``llm_usage`` (every LLM call records there post the boundary-
 * recording fix). Shows the category split (chat / ingestion / profiling) +
 * breakdowns by source / model / provider / user — the hyper-precise per-org
 * costs section. Lazy: the query only fires when this tab is mounted.
 */

export interface OrgCostTabProps {
  orgId: string;
}

const RANGE_PRESETS = [
  { id: "7d", label: msg`7 giorni`, days: 7 },
  { id: "30d", label: msg`30 giorni`, days: 30 },
  { id: "90d", label: msg`90 giorni`, days: 90 },
] as const;

type RangeId = (typeof RANGE_PRESETS)[number]["id"];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

const CATEGORY_META: Record<
  "chat" | "ingestion" | "profiling",
  {
    label: MessageDescriptor;
    icon: typeof MessageSquare;
    bar: string;
    dot: string;
  }
> = {
  chat: {
    label: msg`Chat`,
    icon: MessageSquare,
    bar: "bg-amber-500",
    dot: "bg-amber-500",
  },
  ingestion: {
    label: msg`Ingestione`,
    icon: Database,
    bar: "bg-emerald-500",
    dot: "bg-emerald-500",
  },
  profiling: {
    label: msg`Profiling`,
    icon: Sparkles,
    bar: "bg-ando-orange",
    dot: "bg-ando-orange",
  },
};

function CategorySplitBar({
  split,
}: {
  split: { chat: number; ingestion: number; profiling: number };
}): React.ReactElement {
  const { i18n } = useLingui();
  const total = split.chat + split.ingestion + split.profiling;
  const keys = ["chat", "ingestion", "profiling"] as const;
  return (
    <div className="space-y-3">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-ink-3/10"
        role="img"
        aria-label={i18n._(
          msg`Composizione costo: chat ${formatUsd(split.chat)}, ingestione ${formatUsd(split.ingestion)}, profiling ${formatUsd(split.profiling)}`,
        )}
      >
        {keys.map((k) =>
          split[k] > 0 ? (
            <div
              key={k}
              className={cn("h-full", CATEGORY_META[k].bar)}
              style={{ width: `${total ? (split[k] / total) * 100 : 0}%` }}
            />
          ) : null,
        )}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {keys.map((k) => {
          const Icon = CATEGORY_META[k].icon;
          const pct = total ? (split[k] / total) * 100 : 0;
          return (
            <div key={k} className="flex items-center gap-1.5 text-sm">
              <span className={cn("h-2.5 w-2.5 rounded-full", CATEGORY_META[k].dot)} aria-hidden />
              <Icon className="h-3.5 w-3.5 text-ink-2" aria-hidden />
              <span className="text-ink-1">{i18n._(CATEGORY_META[k].label)}</span>
              <span className="font-medium text-ink-1">{formatUsd(split[k])}</span>
              <span className="text-ink-3">({pct.toFixed(0)}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DimensionTable({
  title,
  rows,
  emptyHint,
}: {
  title: string;
  rows: AdminOrgCostDimensionRow[];
  emptyHint: string;
}): React.ReactElement {
  const { i18n } = useLingui();
  const max = rows.reduce((m, r) => Math.max(m, r.cost_usd), 0);
  return (
    <section className="rounded-xl border border-hairline bg-cream/40 p-4">
      <h4 className="mb-3 font-semibold text-ink-1 text-sm">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-ink-3 text-sm">{emptyHint}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.label} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-ink-1" title={r.label}>
                  {r.label}
                </span>
                <span className="shrink-0 font-medium text-ink-1">{formatUsd(r.cost_usd)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-3/10">
                  <div
                    className="h-full rounded-full bg-ando-orange/70"
                    style={{ width: `${max ? (r.cost_usd / max) * 100 : 0}%` }}
                  />
                </div>
                <span className="shrink-0 text-ink-3 text-xs tabular-nums">
                  {i18n._(msg`${formatInt(r.calls)} call`)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Non-PII stable handle from a user_id UUID. Lets the platform admin read the
 * per-user cost distribution (who's the heavy spender) WITHOUT exposing the
 * customer employee's email — that is GDPR PII, revealed only under audited
 * Inspect Mode. Stable per user so ranking is consistent across refetches.
 */
function userHandle(userId: string): MessageDescriptor {
  const shortId = userId.slice(0, 6);
  return msg`Utente ${shortId}`;
}

function UserTable({
  rows,
  inspectActive,
  orgId,
}: {
  rows: AdminOrgCostUserRow[];
  inspectActive: boolean;
  orgId: string;
}): React.ReactElement {
  const { i18n } = useLingui();
  const [showReveal, setShowReveal] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const activate = useActivateOrgCostInspect(orgId);
  const reasonOk = reason.trim().length >= 10;

  function submitReveal(): void {
    if (!reasonOk) return;
    activate.mutate(reason.trim(), {
      onSuccess: () => {
        setShowReveal(false);
        setReason("");
      },
    });
  }

  return (
    <section className="rounded-xl border border-hairline bg-cream/40 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="font-semibold text-ink-1 text-sm">{i18n._(msg`Costo per utente`)}</h4>
        {inspectActive ? (
          <Badge variant="outline" className="gap-1 text-accent text-xs">
            <ShieldCheck className="h-3 w-3" aria-hidden />
            {i18n._(msg`identità visibili`)}
          </Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowReveal((v) => !v)}
            aria-expanded={showReveal}
            className="h-7 gap-1 text-xs"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            {i18n._(msg`Mostra identità`)}
          </Button>
        )}
      </div>

      {/* Reveal identities — audited Inspect Mode, 15-min TTL (GDPR contract) */}
      {!inspectActive && showReveal && (
        <div className="mb-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs">
          <p className="mb-2 text-ink-2">
            {i18n._(
              msg`Le email sono dati personali dei dipendenti del cliente (GDPR). Per rivelarle indica una motivazione (≥ 10 caratteri): l'accesso è registrato in audit log e scade dopo 15 minuti.`,
            )}
          </p>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={i18n._(msg`Es. verifica anomalia costo ticket #4598`)}
            aria-label={i18n._(msg`Motivo rivelazione identità`)}
            className="h-8 w-full rounded-md border border-hairline bg-card px-2 text-[12px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10.5px] text-ink-3 tabular-nums">{reason.trim().length}/10</span>
            <Button
              size="sm"
              onClick={submitReveal}
              disabled={!reasonOk || activate.isPending}
              className="h-7 gap-1 text-xs"
            >
              {activate.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : null}
              {i18n._(msg`Attiva Inspect (15m)`)}
            </Button>
          </div>
          {activate.isError && (
            <p className="mt-1.5 text-[11px] text-red-600">
              {i18n._(msg`Attivazione non riuscita. Riprova.`)}
            </p>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-ink-3 text-sm">{i18n._(msg`Nessuna attività nel periodo.`)}</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {rows.map((r) => (
            <li
              key={r.user_id ?? "system"}
              className="flex items-center justify-between gap-3 py-2 text-sm"
            >
              <span className="truncate text-ink-1">
                {r.user_id === null
                  ? i18n._(msg`(sistema / background)`)
                  : inspectActive && r.email
                    ? r.email
                    : i18n._(userHandle(r.user_id))}
              </span>
              <span className="flex shrink-0 items-center gap-3">
                <span className="text-ink-3 text-xs tabular-nums">
                  {i18n._(msg`${formatInt(r.calls)} call`)}
                </span>
                <span className="font-medium text-ink-1">{formatUsd(r.cost_usd)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function OrgCostTab({ orgId }: OrgCostTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const [range, setRange] = React.useState<RangeId>("30d");
  const days = RANGE_PRESETS.find((r) => r.id === range)?.days ?? 30;
  const args = React.useMemo(() => ({ start: isoDaysAgo(days) }), [days]);
  const { data, isLoading, isError, refetch } = useAdminOrgCost(orgId, args);

  return (
    <div className="space-y-5">
      {/* Range selector */}
      <div
        className="flex items-center gap-1.5"
        role="group"
        aria-label={i18n._(msg`Intervallo temporale`)}
      >
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setRange(p.id)}
            aria-pressed={range === p.id}
            className={cn(
              "rounded-lg px-3 py-1.5 font-medium text-sm transition-colors",
              range === p.id
                ? "bg-ando-orange/15 text-ando-orange"
                : "text-ink-2 hover:bg-ink-3/10",
            )}
          >
            {i18n._(p.label)}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-12 text-ink-3">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          <span className="text-sm">{i18n._(msg`Caricamento costi…`)}</span>
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-300/70 bg-transparent p-4 text-red-700 text-sm">
          <p>{i18n._(msg`Impossibile caricare i costi dell'organizzazione.`)}</p>
          <button type="button" onClick={() => refetch()} className="mt-2 font-medium underline">
            {i18n._(msg`Riprova`)}
          </button>
        </div>
      )}

      {data && (
        <>
          <AdminKpiGrid kpis={data.kpis} />

          <section className="rounded-xl border border-hairline bg-cream/40 p-4">
            <h4 className="mb-3 font-semibold text-ink-1 text-sm">
              {i18n._(msg`Composizione del costo`)}
            </h4>
            <CategorySplitBar split={data.summary.by_category} />
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-ink-3">{i18n._(msg`Token input`)}</dt>
                <dd className="font-medium text-ink-1 tabular-nums">
                  {formatInt(data.summary.input_tokens)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-3">{i18n._(msg`Token output`)}</dt>
                <dd className="font-medium text-ink-1 tabular-nums">
                  {formatInt(data.summary.output_tokens)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-3">{i18n._(msg`Token cached`)}</dt>
                <dd className="font-medium text-ink-1 tabular-nums">
                  {formatInt(data.summary.cached_input_tokens)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-3">{i18n._(msg`Chiamate LLM`)}</dt>
                <dd className="font-medium text-ink-1 tabular-nums">
                  {formatInt(data.summary.calls)}
                </dd>
              </div>
            </dl>
          </section>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DimensionTable
              title={i18n._(msg`Per fonte`)}
              rows={data.by_source}
              emptyHint={i18n._(msg`Nessuna fonte nel periodo.`)}
            />
            <DimensionTable
              title={i18n._(msg`Per modello`)}
              rows={data.by_model}
              emptyHint={i18n._(msg`Nessun modello nel periodo.`)}
            />
            <DimensionTable
              title={i18n._(msg`Per provider`)}
              rows={data.by_provider}
              emptyHint={i18n._(msg`Nessun provider nel periodo.`)}
            />
            <UserTable rows={data.by_user} inspectActive={data.inspect_mode_active} orgId={orgId} />
          </div>
        </>
      )}
    </div>
  );
}
